import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import DocumentAccessIndicator from "components/ui/DocumentAccessIndicator";

const docCategories = [
  {
    name: "Legal & Structuring",
    icon: "Scale",
    docs: [
      { name: "Project Development Agreement.pdf", size: "2.4 MB", version: "v3.1", date: "12/02/2026", access: "accessible" },
      { name: "Land Lease Agreement.pdf", size: "1.8 MB", version: "v2.0", date: "08/01/2026", access: "accessible" },
      { name: "Grid Connection Agreement.pdf", size: "3.1 MB", version: "v1.2", date: "15/11/2025", access: "restricted" },
    ],
  },
  {
    name: "Financial Models",
    icon: "BarChart2",
    docs: [
      { name: "Base Case Financial Model.xlsx", size: "4.7 MB", version: "v5.2", date: "28/02/2026", access: "accessible" },
      { name: "Sensitivity Analysis.xlsx", size: "2.1 MB", version: "v3.0", date: "20/02/2026", access: "accessible" },
      { name: "Debt Term Sheet.pdf", size: "0.9 MB", version: "v1.0", date: "05/03/2026", access: "pending" },
    ],
  },
  {
    name: "Technical Reports",
    icon: "FileText",
    docs: [
      { name: "Independent Technical Report.pdf", size: "8.2 MB", version: "v2.1", date: "10/01/2026", access: "accessible" },
      { name: "Environmental Impact Assessment.pdf", size: "12.4 MB", version: "v1.0", date: "22/09/2025", access: "accessible" },
      { name: "Grid Study Report.pdf", size: "5.6 MB", version: "v1.3", date: "14/12/2025", access: "restricted" },
    ],
  },
  {
    name: "ESG & Compliance",
    icon: "Leaf",
    docs: [
      { name: "ESG Due Diligence Report.pdf", size: "3.8 MB", version: "v2.0", date: "01/03/2026", access: "accessible" },
      { name: "TCFD Disclosure.pdf", size: "1.2 MB", version: "v1.1", date: "18/02/2026", access: "accessible" },
    ],
  },
];

const accessIcons = {
  accessible: { icon: "FileDown", color: "text-success" },
  restricted: { icon: "Lock", color: "text-error" },
  pending: { icon: "Clock", color: "text-warning" },
};

export default function DocumentsTab() {
  const navigate = useNavigate();
  const [toast, setToast] = useState(null);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const handleDownload = (doc) => {
    if (doc?.access === "accessible") {
      showToast(`Downloading ${doc?.name}…`);
    } else if (doc?.access === "restricted") {
      showToast("Access request submitted. You will be notified within 24 hours.");
    }
  };

  return (
    <div className="space-y-5">
      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 bg-foreground text-background px-4 py-3 rounded-lg shadow-lg font-caption text-sm flex items-center gap-2">
          <Icon name="CheckCircle" size={16} color="var(--color-success)" />
          {toast}
        </div>
      )}
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-heading text-lg font-semibold text-foreground">Data Room Documents</h3>
          <p className="font-caption text-sm text-muted-foreground">Secure document repository with version control</p>
        </div>
        <div className="flex gap-2">
          <DocumentAccessIndicator accessStatus="accessible" documentCount={14} updatedCount={2} />
          <Button variant="outline" iconName="ExternalLink" iconPosition="left" iconSize={14} onClick={() => navigate("/data-room")}>
            Open Data Room
          </Button>
        </div>
      </div>
      {/* Document Categories */}
      {docCategories?.map((cat) => (
        <div key={cat?.name} className="card p-0 overflow-hidden">
          <div className="flex items-center gap-3 px-4 md:px-5 py-3 bg-muted/30 border-b border-border">
            <Icon name={cat?.icon} size={16} color="var(--color-primary)" />
            <h4 className="font-caption font-semibold text-foreground text-sm">{cat?.name}</h4>
            <span className="font-caption text-xs text-muted-foreground ml-auto">{cat?.docs?.length} files</span>
          </div>
          <div className="divide-y divide-border">
            {cat?.docs?.map((doc, i) => {
              const ai = accessIcons?.[doc?.access];
              return (
                <div key={i} className="flex items-center gap-3 px-4 md:px-5 py-3 hover:bg-muted/20 transition-colors">
                  <Icon name="File" size={16} color="var(--color-muted-foreground)" className="flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-caption text-sm font-medium text-foreground truncate">{doc?.name}</p>
                    <div className="flex flex-wrap items-center gap-2 mt-0.5">
                      <span className="font-data text-xs text-muted-foreground">{doc?.size}</span>
                      <span className="font-data text-xs text-muted-foreground">{doc?.version}</span>
                      <span className="font-caption text-xs text-muted-foreground">{doc?.date}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDownload(doc)}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs font-caption font-medium transition-all hover:opacity-80 ${
                      doc?.access === "accessible" ? "bg-success/10 text-success" :
                      doc?.access === "restricted"? "bg-error/10 text-error" : "bg-warning/10 text-warning"
                    }`}
                    title={doc?.access === "restricted" ? "Request access" : doc?.access === "pending" ? "Pending review" : "Download"}
                  >
                    <Icon name={ai?.icon} size={13} />
                    <span className="hidden sm:inline">
                      {doc?.access === "accessible" ? "Download" : doc?.access === "restricted" ? "Request" : "Pending"}
                    </span>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}